import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import { ApiError } from "@incorposano/api-client";

import { App } from "@/App";
import { useAuth } from "@/stores/auth";
import { useSession } from "@/stores/session";
import "@/styles/global.css";

// When the backend returns 401 (invalid/expired token, or the user behind it
// no longer exists in the DB), wipe both stores so RequireAuth bounces to
// /login on the next render. Auth-namespaced endpoints (login, signup, etc.)
// handle their own 401s — we skip those so wrong-password attempts don't log
// the user out of an unrelated session.
function handleAuthError(err: unknown): void {
  if (!(err instanceof ApiError) || err.status !== 401) return;
  if (err.url.includes("/api/v1/auth/")) return;
  if (!useAuth.getState().token) return;
  useSession.getState().reset();
  useAuth.getState().logout();
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => handleAuthError(err),
  }),
  mutationCache: new MutationCache({
    onError: (err) => handleAuthError(err),
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function bootstrap() {
  if (import.meta.env.VITE_USE_MOCKS === "true") {
    const { enableMocks } = await import("@/api/mocks/browser");
    await enableMocks();
  }

  // Register the PWA service worker in production only. Dynamic import so
  // the SW registration path is tree-shaken out of dev builds entirely and
  // does not block first paint (registerSW schedules registration after load).
  if (import.meta.env.PROD) {
    const { registerSW } = await import("virtual:pwa-register");
    registerSW({ immediate: false });
  }

  const rootEl = document.getElementById("root");
  if (!rootEl) throw new Error("Missing #root element in index.html");

  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
